import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Typewriter } from "react-simple-typewriter";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTheme } from "../context/ThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGithub,
  faLinkedin,
  faFacebook,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";
import Footer from "../components/Footer";

// Lazy load components
const MyExpertise = lazy(() => import("../components/ExpertiesComponent"));
const MyCertificateHome = lazy(() => import("../components/MyCertificateHome"));
const Projects = lazy(() => import("../components/Projects"));

// Preload images function
const preloadImages = (imageArray) => {
  return Promise.all(
    imageArray.map((image) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = image;
        img.onload = resolve;
        img.onerror = resolve; // Resolve on error to avoid blocking
      });
    })
  );
};

const HomePage = () => {
  const { darkMode } = useTheme();
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true); // State to track loading
  const { ref: leftRef, inView: leftInView } = useInView({ threshold: 0.1 });
  const { ref: mleftRef, inView: mleftInView } = useInView({ threshold: 0.1 });

  const certificateRef = useRef(null); // Ref for the certificate section

  // Preload images and components
  useEffect(() => {
    const loadContent = async () => {
      // Preload Google Drive images
      await preloadImages([
        "https://lh3.googleusercontent.com/d/1uR9LDDyGF04rbf8dLlixzdT0_CXnZFLW",
        "https://lh3.googleusercontent.com/d/1XOc5odpaSEmxq_COfFNW91FjeHB48PWK"
      ]);
      setLoading(false); // Set loading to false after images are loaded
    };

    loadContent();
  }, []);

  const handleDownloadCv = () => {
    try {
      setDownloading(true);
      const link = document.createElement("a");
      link.href = "https://drive.google.com/uc?export=download&id=11BUFbIF0Rk6TM-ditSLCfnZ132xVzs2k&confirm=t";
      link.download = "Bhishan-sah_Resume_CV.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => setDownloading(false), 1000);
    } catch (error) {
      console.error("Error downloading CV:", error);
      setDownloading(false);
    }
  };

  useEffect(() => {
    document.title = "Bhishan Sah - Web Developer Portfolio";

    // Set meta description
    const metaDescription = document.createElement("meta");
    metaDescription.name = "description";
    metaDescription.content = "My portfolio home page with certificates";
    document.head.appendChild(metaDescription);

    return () => {
      document.head.removeChild(metaDescription);
    };
  }, []);

  const handlePreviewCV = () => {
    window.open('https://drive.google.com/file/d/11BUFbIF0Rk6TM-ditSLCfnZ132xVzs2k/preview', '_blank');
  }
  return (
    <>
      <div className="lg:pt-16 p-2  -mt-8 ">
        {/* Loading Screen */}

        {loading ? (
          <div className="flex  flex-col items-center justify-center w-full h-screen">
            {/* Loading placeholders */}
            {/* Mobile Image Section */}
            <div className="w-full block sm:hidden px-4 mb-8">
              <div className={`bg-gradient-to-r from-purple-600 to-white shadow-md shadow-black/25 rounded-lg mb-8 opacity-65`}>
                <div className="bg-gray-200 h-80 w-full rounded-lg animate-pulse"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile Image Section */}
            <motion.div
              className="w-[95%] mt-8 mx-auto block sm:hidden px-4 mb-6"
              ref={mleftRef}
              initial={{ opacity: 0, x: -100 }}
              animate={mleftInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              <div className={`relative group ${darkMode ? "opacity-90" : ""}`}>
                <div className="relative">
                  <div className=''>
                    <img
                      src="https://lh3.googleusercontent.com/d/1uR9LDDyGF04rbf8dLlixzdT0_CXnZFLW"
                      alt="Profile"
                      className="object-cover h-64 sm:h-80 object-top w-full rounded-md shadow-lg transition-transform duration-300 hover:scale-105"
                    />
                    
                  </div>
                </div>
              </div>
            </motion.div>
            {/* <DigitalClock className=""  darkMode={darkMode} /> */}

            {/* Hero Section */}
            <div className="relative w-full  flex items-center justify-center overflow-hidden">
                          
              {/* Background Elements */}
              <div className="absolute inset-0 -z-10 overflow-hidden">
                <div className={`absolute top-20 left-4 sm:left-10 w-48 h-48 sm:w-72 sm:h-72 ${darkMode ? 'bg-blue-500/10' : 'bg-blue-200/30'} rounded-full blur-3xl animate-pulse`}></div>
                <div className={`absolute bottom-20 right-4 sm:right-10 w-64 h-64 sm:w-96 sm:h-96 ${darkMode ? 'bg-purple-500/10' : 'bg-purple-200/30'} rounded-full blur-3xl animate-pulse delay-1000`}></div>
                <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] ${darkMode ? 'bg-gradient-to-r from-blue-500/5 to-purple-500/5' : 'bg-gradient-to-r from-blue-100/50 to-purple-100/50'} rounded-full blur-3xl`}></div>
              </div>

              <div className="container mx-auto px-0 sm:px-0 py-6 my-6 lg:px-8  w-[85%] flex flex-col lg:flex-row items-center justify-between gap-8 sm:gap-12 lg:gap-16">
                
                {/* Left Side - Profile Image */}
                <motion.div
                  className="lg:w-2/5 w-full  justify-center  order-2 lg:order-1 hidden sm:flex"
                  ref={leftRef}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={leftInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
                >
                  <div className="relative group">
                    {/* Animated rings */}
                    <div className={`absolute inset-0 rounded-full ${darkMode ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-gradient-to-r from-blue-400 to-purple-400'} p-1 animate-spin-slow`}>
                      <div className={`w-full h-full rounded-full ${darkMode ? 'bg-gray-900' : 'bg-white'}`}></div>
                    </div>
                    
                    {/* Glow effect */}
                    <div className={`absolute inset-0 rounded-full ${darkMode ? 'bg-blue-500/20' : 'bg-blue-400/20'} blur-xl scale-110 group-hover:scale-125 transition-transform duration-500`}></div>
                    
                    {/* Main image container */}
                    <div className="relative w-56 h-56 sm:w-64 sm:h-64 md:w-80 md:h-80 lg:w-96 lg:h-96">
                      <img
                        src="https://lh3.googleusercontent.com/d/1XOc5odpaSEmxq_COfFNW91FjeHB48PWK"
                        alt="Bhishan Prasad Sah"
                        className="w-full h-full object-cover object-top rounded-full border-4 border-white shadow-2xl group-hover:scale-105 transition-transform duration-500"
                      />
                      
                      {/* Floating elements */}
                      <div className={`absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-8 h-8 sm:w-12 sm:h-12 ${darkMode ? 'bg-blue-500' : 'bg-blue-400'} rounded-full flex items-center justify-center shadow-lg animate-bounce`}>
                        <i className="fa-solid fa-code text-white text-sm sm:text-lg"></i>
                      </div>
                      <div className={`absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-8 h-8 sm:w-12 sm:h-12 ${darkMode ? 'bg-purple-500' : 'bg-purple-400'} rounded-full flex items-center justify-center shadow-lg animate-bounce delay-500`}>
                        <i className="fa-solid fa-palette text-white text-sm sm:text-lg"></i>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Right Side - Content */}
                <motion.div
                  className="lg:w-1/2  w-full text-center lg:text-left order-1 lg:order-2 px-2 sm:px-0"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  {/* Greeting */}
                  <motion.div
                    className=""
                    initial={{ y: -30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                  >
                    <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${darkMode ? 'bg-gray-800 text-blue-400 border border-gray-700' : 'bg-blue-50 text-blue-600 border border-blue-200'}`}>
                      👋 Hello, I&apos;m
                    </span>
                  </motion.div>

                  {/* Name */}
                  <motion.h1
                    className={`text-2xl lg:text-5xl uppercase font-bold  leading-tight ${
                      darkMode 
                        ? 'bg-gradient-to-r from-white via-blue-200 to-purple-200 text-transparent bg-clip-text' 
                        : 'bg-gradient-to-r from-gray-900 via-blue-600 to-purple-600 text-transparent bg-clip-text'
                    }`}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                  >
                    Bhishan Prasad Sah
                  </motion.h1>

                  {/* Title */}
                  <motion.div
                    className="mb-8 -mt-1 "
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                  >
                    <p className={`text-md  font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Computer Engineer
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-2 sm:gap-3 text-xl sm:text-2xl md:text-3xl font-semibold">
                      <span className={`${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>I&apos;m a</span>
                      <span className={`${darkMode ? 'text-purple-400' : 'text-purple-600'} min-w-[180px] sm:min-w-[200px] text-center sm:text-left`}>
                        <Typewriter
                          words={[
                            "Web Developer",
                            "UI/UX Designer", 
                            "Photographer",
                            "Videographer",
                          ]}
                          loop={true}
                          cursor
                          cursorStyle="|"
                          typeSpeed={80}
                          deleteSpeed={50}
                          delaySpeed={2000}
                        />
                      </span>
                    </div>
                  </motion.div>

                  {/* Description */}
                  <motion.p
                    className={`text-sm text-justify md:text-xl leading-relaxed mb-8 sm:mb-10 max-w-2xl sm:px-0 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                  >
                   A motivated Full-Stack Developer specializing in the MERN stack, with experience in building responsive,
full-featured web applications. Skilled in transforming ideas into deployable products using modern tools
and frameworks, with a keen interest in problem-solving and continuous learning. 
                  </motion.p>

                  {/* CTA Buttons */}
                  <motion.div
                    className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start px-2 sm:px-0 w-full max-w-md lg:max-w-none"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1, duration: 0.6 }}
                  >
                    <motion.button
                      onClick={handleDownloadCv}
                      disabled={downloading}
                      className={`group relative px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-white overflow-hidden transition-all duration-300 text-sm sm:text-base ${
                        downloading ? "cursor-not-allowed opacity-70" : ""
                      } ${darkMode ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500' : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'} shadow-lg hover:shadow-xl`}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="relative z-10 flex items-center gap-2 sm:gap-3">
                        <i className={`fa-solid ${downloading ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                        {downloading ? "Downloading..." : "Download CV"}
                      </span>
                      <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                    </motion.button>

                    <motion.button
                      onClick={() => 
                        handlePreviewCV()
                      }
                      className={`group px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold border-2 transition-all duration-300 text-sm sm:text-base ${
                        darkMode 
                          ? 'border-gray-600 text-gray-300 hover:border-blue-400 hover:text-blue-400 hover:bg-blue-400/10' 
                          : 'border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50'
                      }`}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="flex items-center gap-2 sm:gap-3">
                        <i className="fa-solid fa-eye"></i>
                        Preview CV
                      </span>
                    </motion.button>
                  </motion.div>

                  {/* Social Links */}
                  <motion.div
                    className="flex gap-3 sm:gap-4 justify-center lg:justify-start mt-2 "
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.2, duration: 0.6 }}
                  >
                    {[
                      { icon: faGithub, href: 'https://github.com/visionmax01', color: 'hover:text-gray-900' },
                      { icon: faLinkedin, href: 'https://www.linkedin.com/in/bhishansah/', color: 'hover:text-blue-600' },
                      { icon: faFacebook, href: 'https://www.facebook.com/share/qJntLSmzjo3uB8HV/?mibextid=qi2Omg', color: 'hover:text-blue-600' },
                      { icon: faInstagram, href: 'https://www.instagram.com/bhishansah/', color: 'hover:text-pink-500' }
                    ].map((social, index) => (
                      <motion.a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-10 h-10 sm:w-12 my-2 sm:h-12 rounded-lg ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'} flex items-center justify-center transition-all duration-300 ${social.color} hover:scale-110 hover:shadow-lg`}
                        whileHover={{ y: 0 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FontAwesomeIcon icon={social.icon} className="text-sm sm:text-lg" />
                      </motion.a>
                    ))}
                  </motion.div>
                </motion.div>
              </div>
            </div>

            <div className="w-full max-w-full mt-12 sm:mt-16 overflow-hidden">
              {/* Expertise Section */}
              <Suspense fallback={<div className="animate-pulse bg-gray-200 h-20 rounded w-full mb-4"></div>}>
                <MyExpertise darkMode={darkMode} />
              </Suspense>
            </div>

            <div className="mb-6 w-full max-w-full overflow-hidden" ref={certificateRef}>
              {/* Certificate Section */}
              <Suspense fallback={<div className="animate-pulse h-20 rounded w-full mb-4"></div>}>
                <MyCertificateHome darkMode={darkMode} />
              </Suspense>
            </div>

            <div className="mb-12 relative w-full max-w-full overflow-hidden">
              {/* Projects Section */}
              <Suspense fallback={<div className="animate-pulse bg-gray-200 h-20 rounded w-full mb-4"></div>}>
                <div className="relative">
                 
                  <div className={`absolute -z-10 top-0 left-1/2 -translate-x-1/2 w-48 h-48 
                    ${darkMode ? "bg-gray-700" : "bg-blue-200"} 
                    rounded-full blur-[80px] opacity-50`}>
                  </div>
                </div>
                <Projects darkMode={darkMode} />
              </Suspense>
            </div>
          </>
        )}
      </div>
      <Footer darkMode={darkMode}  />
    </>
  );
};

export default HomePage;
